import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Plus, MapPin, ChevronLeft, ChevronRight, Plane,
  Download, Settings, Sparkles, Share2, Copy, ClipboardCopy,
  RefreshCw, AlertCircle, Cloud, CloudOff, Moon, Heart,
} from "lucide-react";

import {
  STATUSES, TIME_SLOTS, resolveIcon, DEFAULT_CATEGORIES,
} from "./constants.js";
import {
  toISO, fromISO, formatDayShort,
  computeTripBounds, computeAllDays, makeLegForDate,
} from "./utils/date.js";
import {
  parseTripUrl, setTripUrl, getDateFromHash, setDateHash,
} from "./utils/url.js";
import {
  createTrip, fetchTrip, addLeg as apiAddLeg, patchLeg as apiPatchLeg, deleteLeg as apiDeleteLeg,
  createItem as apiCreateItem, patchItem as apiPatchItem, deleteItem as apiDeleteItem,
  duplicateTrip as apiDuplicateTrip, ApiError, NetworkError,
} from "./api/client.js";
import {
  enqueueWrite, drainQueue, getQueueSize,
} from "./api/queue.js";

import LandingFlow from "./components/Landing/LandingFlow.jsx";
import TimelineCalendar from "./components/Calendar/TimelineCalendar.jsx";
import DayHero from "./components/DayHero.jsx";
import EmptyState from "./components/EmptyState.jsx";
import ItemCard from "./components/ItemCard.jsx";
import ItemModal from "./components/Modals/ItemModal.jsx";
import LegsModal from "./components/Modals/LegsModal.jsx";
import ExportModal from "./components/Modals/ExportModal.jsx";
import ShareModal from "./components/Modals/ShareModal.jsx";
import DuplicateModal from "./components/Modals/DuplicateModal.jsx";
import MumModeModal from "./components/Modals/MumModeModal.jsx";

const POLL_INTERVAL_MS = 30_000;
const PATCH_DEBOUNCE_MS = 300;

/*
 * Lucide's Plane icon has its nose at the upper-right (~21,3 in 24×24 viewBox),
 * so it naturally points NE at ~45°.  Plane 2 is the same icon rotated 180°,
 * pointing SW.  Contrails are drawn with an overflow:visible SVG centred on
 * each plane; they extend in the opposite direction of the nose.
 */
function AirplaneLoader({ exiting }) {
  const [bobbing, setBobbing] = useState(false);

  useEffect(() => {
    if (exiting) return;
    const t = setTimeout(() => setBobbing(true), 1700);
    return () => clearTimeout(t);
  }, [exiting]);

  const p1Anim = exiting
    ? 'ap-plane1-exit 0.85s cubic-bezier(0.55,0,1,0.45) forwards'
    : 'ap-plane1-enter 1.6s cubic-bezier(0.34,1.12,0.64,1) forwards';
  const p2Anim = exiting
    ? 'ap-plane2-exit 0.85s cubic-bezier(0.55,0,1,0.45) 0.1s forwards'
    : 'ap-plane2-enter 1.6s cubic-bezier(0.34,1.12,0.64,1) 0.2s forwards';

  // Each plane sits inside a 80×80 box; the contrail SVG uses overflow:visible
  // and is centred on that box.  Coordinates below are relative to box centre (0,0).
  // Plane 1 nose → NE  → contrail streams go SW  (negative x, positive y)
  // Plane 2 nose → SW  → contrail streams go NE  (positive x, negative y)
  const Contrail = ({ id, dx, dy, t1, t2 }) => (
    <svg
      width="1" height="1"
      style={{ position: 'absolute', top: '50%', left: '50%', overflow: 'visible', transform: 'translate(-50%,-50%)', zIndex: 0 }}
      fill="none"
    >
      <defs>
        <linearGradient id={id}
          x1={dx * -20} y1={dy * 20}
          x2={dx * -140} y2={dy * 140}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#94a3b8" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* upper engine stream */}
      <path
        d={`M ${dx * -18} ${dy * 18} L ${dx * -135} ${dy * 135}`}
        stroke={`url(#${id})`} strokeWidth="4" strokeLinecap="round"
        style={{ animation: t1 }}
      />
      {/* lower engine stream, slightly offset */}
      <path
        d={`M ${dx * -28} ${dy * 8} L ${dx * -145} ${dy * 125}`}
        stroke={`url(#${id})`} strokeWidth="3" strokeLinecap="round"
        style={{ animation: t2 }}
      />
    </svg>
  );

  return (
    <div
      className="fixed inset-0 bg-stone-50 overflow-hidden"
      style={{ zIndex: 50, animation: exiting ? 'ap-bg-out 0.45s ease 0.65s forwards' : 'none' }}
    >
      {/* Plane 1 – NE heading, lower-left of screen centre */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(calc(-40px - 12vw), calc(-40px + 8vh))',
      }}>
        <div style={{ animation: p1Anim, opacity: 0 }}>
          <div style={{ animation: bobbing && !exiting ? 'ap-bob 3s ease-in-out infinite' : 'none' }}>
            <div style={{ position: 'relative', width: 80, height: 80 }}>
              <Contrail id="ctg1" dx={1} dy={1}
                t1="ap-trail-pulse 2s ease-in-out infinite"
                t2="ap-trail-pulse 2s ease-in-out 0.55s infinite"
              />
              <Plane size={80} style={{ color: '#334155', position: 'relative', zIndex: 1 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Plane 2 – SW heading (rotated 180°), upper-right of screen centre */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(calc(-40px + 8vw), calc(-40px - 8vh))',
      }}>
        <div style={{ animation: p2Anim, opacity: 0 }}>
          <div style={{ animation: bobbing && !exiting ? 'ap-bob 3s ease-in-out 1.5s infinite' : 'none' }}>
            <div style={{ position: 'relative', width: 80, height: 80 }}>
              <Contrail id="ctg2" dx={-1} dy={-1}
                t1="ap-trail-pulse 2s ease-in-out 0.2s infinite"
                t2="ap-trail-pulse 2s ease-in-out 0.75s infinite"
              />
              <Plane size={80} style={{ color: '#334155', position: 'relative', zIndex: 1, transform: 'rotate(180deg)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Loading text */}
      <p style={{
        position: 'absolute',
        bottom: '32%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontFamily: "'Fraunces', Georgia, serif",
        fontSize: '14px',
        color: '#94a3b8',
        whiteSpace: 'nowrap',
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.3s ease',
      }}>
        Loading your trip…
      </p>
    </div>
  );
}

function newTempId() {
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function legSignature(leg) {
  return JSON.stringify({
    city: leg.city, country: leg.country, start: leg.start, end: leg.end,
    emoji: leg.emoji, colour: leg.colour, tentative: !!leg.tentative,
    sortOrder: leg.sortOrder ?? 0,
  });
}

export default function App() {
  // ---------- Mode detection ----------
  const [{ mode, tripId, legIds }, setRoute] = useState(() => {
    const parsed = parseTripUrl();
    if (parsed.tripId && parsed.legIds.length > 0) {
      return { mode: "cloud", tripId: parsed.tripId, legIds: parsed.legIds };
    }
    return { mode: "landing", tripId: null, legIds: [] };
  });

  // ---------- Landing state ----------
  const [creating, setCreating] = useState(false);
  const [creationError, setCreationError] = useState(null);

  // ---------- Cloud state ----------
  const [loading, setLoading] = useState(mode === "cloud");
  const [loadError, setLoadError] = useState(null);

  // ---------- Splash state ----------
  // 'loading' → 'exiting' → 'done'
  // Two separate effects so the exiting→done timeout is never cancelled by the
  // splashState dep changing when we call setSplashState("exiting").
  const [splashState, setSplashState] = useState(() => mode === "cloud" ? "loading" : "done");
  useEffect(() => {
    if (!loading && !loadError) {
      setSplashState(s => s === "loading" ? "exiting" : s);
    }
  }, [loading, loadError]);
  useEffect(() => {
    if (splashState !== "exiting") return;
    const t = setTimeout(() => setSplashState("done"), 1100);
    return () => clearTimeout(t);
  }, [splashState]);
  const [tripTitle, setTripTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [legs, setLegs] = useState([]);
  const [items, setItems] = useState({});
  const [toast, setToast] = useState(null); // { kind, message }
  const [queueSize, setQueueSize] = useState(() => getQueueSize());
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  // ---------- Dark mode ----------
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("tripPlannerDarkMode") === "true");
  useEffect(() => { localStorage.setItem("tripPlannerDarkMode", darkMode); }, [darkMode]);

  // ---------- UI state ----------
  const [legsModalOpen, setLegsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [mumModeOpen, setMumModeOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [calendarView, setCalendarView] = useState("timeline");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState(null);
  const [exportPayload, setExportPayload] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => getDateFromHash() || null);
  const [timelineScrollTrigger, setTimelineScrollTrigger] = useState(0);
  const [savingActivity, setSavingActivity] = useState(0); // pending writes in flight
  const dayHeroRef = useRef(null);

  // ---------- Toast helper ----------
  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast((cur) => (cur && cur.message === message ? null : cur)), 4000);
  }, []);

  // ---------- Apply server snapshot ----------
  const applyServerSnapshot = useCallback((data) => {
    setTripTitle(data.trip.title);
    setCategories(data.trip.categories);
    const orderedLegIds = legIds.length ? legIds : data.legs.map((l) => l.id);
    const legsById = Object.fromEntries(data.legs.map((l) => [l.id, l]));
    const orderedLegs = orderedLegIds
      .map((id) => legsById[id])
      .filter(Boolean)
      .map((l) => ({
        id: l.id,
        city: l.city,
        country: l.country,
        start: l.start,
        end: l.end,
        emoji: l.emoji,
        colour: l.colour,
        tentative: !!l.tentative,
        sortOrder: l.sortOrder ?? 0,
      }));
    setLegs(orderedLegs);

    const itemMap = {};
    for (const leg of data.legs) {
      for (const it of leg.items || []) {
        itemMap[it.id] = {
          id: it.id,
          legId: leg.id,
          title: it.title,
          category: it.category,
          status: it.status,
          timeSlot: it.timeSlot,
          date: it.date,
          endDate: it.endDate || "",
          notes: it.notes || "",
          link: it.link || "",
          location: it.location || "",
          createdAt: it.createdAt ? new Date(it.createdAt).getTime() : Date.now(),
          updatedAt: it.updatedAt ? new Date(it.updatedAt).getTime() : Date.now(),
        };
      }
    }
    setItems(itemMap);
  }, [legIds]);

  // ---------- Initial load (cloud mode) ----------
  useEffect(() => {
    if (mode !== "cloud" || !tripId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await fetchTrip(tripId, legIds);
        if (cancelled) return;
        applyServerSnapshot(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) {
          setLoadError("Couldn't connect. Check your connection and try again.");
        } else if (err instanceof ApiError && err.code === "NOT_FOUND") {
          setLoadError("Trip not found. The URL may be wrong, or the trip was deleted.");
        } else {
          setLoadError(err.message || "Failed to load trip.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, tripId, applyServerSnapshot]);

  // ---------- Sync selectedDate → URL hash ----------
  useEffect(() => {
    setDateHash(selectedDate);
  }, [selectedDate]);

  // ---------- Sync URL hash → selectedDate (back/forward navigation) ----------
  useEffect(() => {
    function onHashChange() {
      const date = getDateFromHash();
      if (date) setSelectedDate(date);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // ---------- Default selectedDate ----------
  useEffect(() => {
    if (legs.length === 0) return;
    const sorted = [...legs].sort((a, b) => a.start.localeCompare(b.start));
    const first = sorted[0].start;
    const last = sorted[sorted.length - 1].end;
    const today = toISO(new Date());
    const todayInTrip = today >= first && today <= last;
    setSelectedDate((cur) => {
      if (cur && cur >= today && cur >= first && cur <= last) return cur;
      return todayInTrip ? today : first;
    });
  }, [legs]);

  // ---------- Derived ----------
  const { tripStart, tripEnd, allDays, legForDate } = useMemo(() => {
    const { start, end } = computeTripBounds(legs);
    const _allDays = computeAllDays(start, end);
    return {
      tripStart: start,
      tripEnd: end,
      allDays: _allDays,
      legForDate: makeLegForDate(legs),
    };
  }, [legs]);

  const itemsByDate = useMemo(() => {
    const map = {};
    Object.values(items).forEach((it) => {
      const dates = [];
      if (it.endDate && it.endDate > it.date) {
        let d = fromISO(it.date);
        const end = fromISO(it.endDate);
        while (d <= end) {
          dates.push(toISO(d));
          d = new Date(d);
          d.setDate(d.getDate() + 1);
        }
      } else {
        dates.push(it.date);
      }
      const totalDays = dates.length;
      dates.forEach((iso, i) => {
        if (!map[iso]) map[iso] = [];
        map[iso].push({
          ...it,
          _dayIndex: i,
          _totalDays: totalDays,
          _isFirst: i === 0,
          _isLast: i === totalDays - 1,
          _isMiddle: totalDays > 1 && i > 0 && i < totalDays - 1,
        });
      });
    });
    const slotOrder = TIME_SLOTS.map((s) => s.id);
    const catOrder = Object.fromEntries(categories.map((c) => [c.id, c.sortOrder]));
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const ca = catOrder[a.category] ?? 99;
        const cb = catOrder[b.category] ?? 99;
        if (ca !== cb) return ca - cb;
        const sa = slotOrder.indexOf(a.timeSlot);
        const sb = slotOrder.indexOf(b.timeSlot);
        if (sa !== sb) return sa - sb;
        return (a.createdAt || 0) - (b.createdAt || 0);
      });
    });
    return map;
  }, [items, categories]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99)),
    [categories]
  );

  const dayItems = (selectedDate ? (itemsByDate[selectedDate] || []) : []).filter((it) => !filter || it.category === filter);
  const groupedDay = sortedCategories.map((cat) => ({
    cat,
    items: dayItems.filter((it) => it.category === cat.id),
  })).filter((g) => g.items.length > 0);

  const leg = selectedDate ? legForDate(fromISO(selectedDate)) : null;

  // ---------- Active polling pause flag ----------
  const anyModalOpen = legsModalOpen || shareModalOpen || duplicateModalOpen
    || modalOpen || !!exportPayload || settingsMenuOpen || mumModeOpen;
  const anyModalOpenRef = useRef(anyModalOpen);
  useEffect(() => { anyModalOpenRef.current = anyModalOpen; }, [anyModalOpen]);

  const isInputFocusedRef = useRef(false);
  useEffect(() => {
    const onFocusIn = () => {
      const tag = document.activeElement?.tagName;
      isInputFocusedRef.current = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    };
    const onFocusOut = () => { isInputFocusedRef.current = false; };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // ---------- Refresh from server (used by polling and online events) ----------
  const refresh = useCallback(async () => {
    if (mode !== "cloud" || !tripId) return;
    try {
      const data = await fetchTrip(tripId, legIds);
      // Merge: replace legs and items by server values, but skip items currently being edited.
      // Since edits are debounced (300ms), the chance of conflict is small. The PRD says:
      // "merge in fields that aren't currently being edited". We approximate this by: if a
      // pending patch exists for an item id, keep the local copy.
      setTripTitle(data.trip.title);
      setCategories(data.trip.categories);
      const orderedLegIds = legIds;
      const legsById = Object.fromEntries(data.legs.map((l) => [l.id, l]));
      const orderedLegs = orderedLegIds.map((id) => legsById[id]).filter(Boolean).map((l) => ({
        id: l.id, city: l.city, country: l.country, start: l.start, end: l.end,
        emoji: l.emoji, colour: l.colour, tentative: !!l.tentative,
        sortOrder: l.sortOrder ?? 0,
      }));
      setLegs((prev) => {
        const localPending = pendingLegPatchesRef.current;
        return orderedLegs.map((l) => localPending.has(l.id) ? (prev.find((p) => p.id === l.id) || l) : l);
      });
      setItems((prev) => {
        const next = {};
        const pendingItems = pendingItemPatchesRef.current;
        const tempItems = Object.values(prev).filter((it) => String(it.id).startsWith("tmp_"));
        for (const leg of data.legs) {
          for (const it of leg.items || []) {
            if (pendingItems.has(it.id) && prev[it.id]) {
              next[it.id] = prev[it.id];
            } else {
              next[it.id] = {
                id: it.id, legId: leg.id, title: it.title, category: it.category,
                status: it.status, timeSlot: it.timeSlot, date: it.date,
                endDate: it.endDate || "", notes: it.notes || "", link: it.link || "", location: it.location || "",
                createdAt: it.createdAt ? new Date(it.createdAt).getTime() : Date.now(),
                updatedAt: it.updatedAt ? new Date(it.updatedAt).getTime() : Date.now(),
              };
            }
          }
        }
        for (const tmp of tempItems) next[tmp.id] = tmp;
        return next;
      });
    } catch (err) {
      // Silent on poll failure; keep stale data.
      if (err instanceof NetworkError) setIsOnline(false);
    }
  }, [mode, tripId, legIds]);

  // ---------- Polling ----------
  useEffect(() => {
    if (mode !== "cloud") return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      const visible = !document.hidden;
      if (visible && !anyModalOpenRef.current && !isInputFocusedRef.current && navigator.onLine) {
        await refresh();
      }
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [mode, refresh]);

  useEffect(() => {
    const onFocus = () => { if (mode === "cloud") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [mode, refresh]);

  // ---------- Pending patch trackers ----------
  const itemPatchTimersRef = useRef(new Map()); // itemId -> { timer, patch }
  const pendingItemPatchesRef = useRef(new Set()); // itemId currently flushing
  const pendingLegPatchesRef = useRef(new Set()); // legId currently flushing

  const incActivity = () => setSavingActivity((n) => n + 1);
  const decActivity = () => setSavingActivity((n) => Math.max(0, n - 1));

  // ---------- Item operations ----------
  const flushItemPatch = useCallback(async (itemId) => {
    const entry = itemPatchTimersRef.current.get(itemId);
    if (!entry) return;
    itemPatchTimersRef.current.delete(itemId);
    pendingItemPatchesRef.current.add(itemId);
    incActivity();
    try {
      const data = await apiPatchItem(itemId, entry.patch);
      setItems((prev) => prev[itemId]
        ? { ...prev, [itemId]: { ...prev[itemId], ...mergeItemFromServer(data.item) } }
        : prev);
    } catch (err) {
      if (err instanceof NetworkError) {
        enqueueWrite({ kind: "patchItem", itemId, patch: entry.patch });
        setQueueSize(getQueueSize());
        showToast("warn", "Offline — changes queued for later");
      } else {
        showToast("error", err.message || "Couldn't save changes");
      }
    } finally {
      pendingItemPatchesRef.current.delete(itemId);
      decActivity();
    }
  }, [showToast]);

  const debouncePatchItem = useCallback((itemId, patch) => {
    const timers = itemPatchTimersRef.current;
    const existing = timers.get(itemId);
    if (existing?.timer) clearTimeout(existing.timer);
    const merged = { ...(existing?.patch ?? {}), ...patch };
    const timer = setTimeout(() => flushItemPatch(itemId), PATCH_DEBOUNCE_MS);
    timers.set(itemId, { timer, patch: merged });
  }, [flushItemPatch]);

  function mergeItemFromServer(it) {
    return {
      id: it.id,
      legId: it.legId,
      title: it.title,
      category: it.category,
      status: it.status,
      timeSlot: it.timeSlot,
      date: it.date,
      endDate: it.endDate || "",
      notes: it.notes || "",
      link: it.link || "",
      location: it.location || "",
      createdAt: it.createdAt ? new Date(it.createdAt).getTime() : Date.now(),
      updatedAt: it.updatedAt ? new Date(it.updatedAt).getTime() : Date.now(),
    };
  }

  const handleSave = async (data) => {
    if (mode !== "cloud") return;
    if (editing) {
      const itemId = editing.id;
      const optimistic = { ...items[itemId], ...data };
      setItems((prev) => ({ ...prev, [itemId]: optimistic }));
      debouncePatchItem(itemId, data);
    } else {
      const targetLeg = legForDate(data.date);
      if (!targetLeg) {
        showToast("error", "Pick a date inside one of your legs");
        return;
      }
      const tempId = newTempId();
      const optimistic = {
        id: tempId, legId: targetLeg.id, ...data,
        createdAt: Date.now(), updatedAt: Date.now(),
      };
      setItems((prev) => ({ ...prev, [tempId]: optimistic }));
      incActivity();
      try {
        const result = await apiCreateItem(targetLeg.id, {
          title: data.title, category: data.category, status: data.status,
          timeSlot: data.timeSlot, date: data.date, endDate: data.endDate || null,
          notes: data.notes, link: data.link, location: data.location,
        });
        setItems((prev) => {
          const { [tempId]: _, ...rest } = prev;
          const real = mergeItemFromServer(result.item);
          return { ...rest, [real.id]: real };
        });
      } catch (err) {
        if (err instanceof NetworkError) {
          enqueueWrite({
            kind: "createItem", legId: targetLeg.id,
            payload: {
              title: data.title, category: data.category, status: data.status,
              timeSlot: data.timeSlot, date: data.date, endDate: data.endDate || null,
              notes: data.notes, link: data.link, location: data.location,
            },
          });
          setQueueSize(getQueueSize());
          showToast("warn", "Offline — new item queued for later");
        } else {
          setItems((prev) => {
            const { [tempId]: _, ...rest } = prev;
            return rest;
          });
          showToast("error", err.message || "Couldn't create item");
        }
      } finally {
        decActivity();
      }
    }
    setModalOpen(false);
    setEditing(null);
    if (data.date && data.date !== selectedDate) setSelectedDate(data.date);
  };

  const handleDelete = async (id) => {
    if (mode !== "cloud") return;
    const snapshot = items[id];
    if (!snapshot) return;
    const confirmed = window.confirm(`Delete "${snapshot.title}"?`);
    if (!confirmed) return;
    setItems((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
    if (String(id).startsWith("tmp_")) return; // not yet persisted
    incActivity();
    try {
      await apiDeleteItem(id);
    } catch (err) {
      if (err instanceof NetworkError) {
        enqueueWrite({ kind: "deleteItem", itemId: id });
        setQueueSize(getQueueSize());
        showToast("warn", "Offline — delete queued for later");
      } else if (err instanceof ApiError && err.code === "NOT_FOUND") {
        // already gone — accept
      } else {
        setItems((prev) => ({ ...prev, [id]: snapshot }));
        showToast("error", err.message || "Couldn't delete item");
      }
    } finally {
      decActivity();
    }
  };

  const handleCycleStatus = (id) => {
    const cur = items[id];
    if (!cur) return;
    const idx = STATUSES.findIndex((s) => s.id === cur.status);
    const next = STATUSES[(idx + 1) % STATUSES.length].id;
    setItems((prev) => ({ ...prev, [id]: { ...cur, status: next } }));
    if (String(id).startsWith("tmp_")) return;
    debouncePatchItem(id, { status: next });
  };

  // ---------- Legs save (diff + dispatch) ----------
  const handleLegsSave = async (newLegs) => {
    if (mode !== "cloud") return;
    const oldById = Object.fromEntries(legs.map((l) => [l.id, l]));
    const newById = Object.fromEntries(newLegs.map((l) => [l.id, l]));

    // Optimistic: apply locally first
    const renumbered = newLegs.map((l, i) => ({ ...l, sortOrder: i }));
    setLegs(renumbered);

    const tasks = [];
    // Removed legs
    for (const oldLeg of legs) {
      if (!newById[oldLeg.id]) {
        tasks.push((async () => {
          incActivity();
          try {
            await apiDeleteLeg(oldLeg.id);
            // Drop items belonging to this leg locally
            setItems((prev) => {
              const next = { ...prev };
              for (const id of Object.keys(next)) if (next[id].legId === oldLeg.id) delete next[id];
              return next;
            });
            // Update URL to drop the legId
            const remaining = legIds.filter((id) => id !== oldLeg.id);
            setRoute((cur) => ({ ...cur, legIds: remaining }));
            setTripUrl(tripId, remaining);
          } catch (err) {
            handleWriteError(err, { kind: "deleteLeg", legId: oldLeg.id });
          } finally { decActivity(); }
        })());
      }
    }
    // Added legs
    for (let i = 0; i < newLegs.length; i++) {
      const cand = newLegs[i];
      if (!oldById[cand.id]) {
        const tempId = cand.id;
        const payload = {
          city: cand.city, country: cand.country, start: cand.start, end: cand.end,
          emoji: cand.emoji, colour: cand.colour, tentative: !!cand.tentative, sortOrder: i,
        };
        tasks.push((async () => {
          incActivity();
          try {
            const { legId } = await apiAddLeg(tripId, payload);
            // Replace temp id with real
            setLegs((prev) => prev.map((l) => l.id === tempId ? { ...l, id: legId, sortOrder: i } : l));
            // Add the new legId to URL
            const newLegIds = [...legIds, legId];
            setRoute((cur) => ({ ...cur, legIds: newLegIds }));
            setTripUrl(tripId, newLegIds);
          } catch (err) {
            if (err instanceof NetworkError) {
              enqueueWrite({ kind: "addLeg", tripId, payload });
              setQueueSize(getQueueSize());
              showToast("warn", "Offline — leg queued for later");
            } else {
              setLegs((prev) => prev.filter((l) => l.id !== tempId));
              showToast("error", err.message || "Couldn't add leg");
            }
          } finally { decActivity(); }
        })());
      }
    }
    // Updated legs
    for (let i = 0; i < newLegs.length; i++) {
      const cand = newLegs[i];
      const old = oldById[cand.id];
      if (!old) continue;
      const candWithOrder = { ...cand, sortOrder: i };
      const oldWithOrder = { ...old, sortOrder: legs.findIndex((l) => l.id === old.id) };
      if (legSignature(candWithOrder) === legSignature(oldWithOrder)) continue;
      const patch = {
        city: cand.city, country: cand.country, start: cand.start, end: cand.end,
        emoji: cand.emoji, colour: cand.colour, tentative: !!cand.tentative, sortOrder: i,
      };
      pendingLegPatchesRef.current.add(cand.id);
      tasks.push((async () => {
        incActivity();
        try {
          await apiPatchLeg(cand.id, patch);
        } catch (err) {
          handleWriteError(err, { kind: "patchLeg", legId: cand.id, patch });
        } finally {
          pendingLegPatchesRef.current.delete(cand.id);
          decActivity();
        }
      })());
    }
    await Promise.all(tasks);
  };

  function handleWriteError(err, descriptor) {
    if (err instanceof NetworkError) {
      enqueueWrite(descriptor);
      setQueueSize(getQueueSize());
      showToast("warn", "Offline — change queued for later");
    } else {
      showToast("error", err.message || "Save failed");
    }
  }

  // ---------- Online / queue drain ----------
  useEffect(() => {
    const onOnline = async () => {
      setIsOnline(true);
      try {
        await drainQueue({
          patchItem: ({ itemId, patch }) => apiPatchItem(itemId, patch),
          createItem: async ({ legId, payload }) => { await apiCreateItem(legId, payload); },
          deleteItem: async ({ itemId }) => { await apiDeleteItem(itemId); },
          patchLeg: ({ legId, patch }) => apiPatchLeg(legId, patch),
          deleteLeg: async ({ legId }) => { await apiDeleteLeg(legId); },
          addLeg: async ({ tripId: t, payload }) => { await apiAddLeg(t, payload); },
        });
        setQueueSize(getQueueSize());
        if (mode === "cloud") refresh();
      } catch {
        // nothing
      }
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [mode, refresh]);

  // ---------- Day nav ----------
  const goPrev = () => {
    const idx = allDays.findIndex((d) => toISO(d) === selectedDate);
    if (idx > 0) {
      setSelectedDate(toISO(allDays[idx - 1]));
      setTimelineScrollTrigger((t) => t + 1);
    }
  };
  const goNext = () => {
    const idx = allDays.findIndex((d) => toISO(d) === selectedDate);
    if (idx < allDays.length - 1) {
      setSelectedDate(toISO(allDays[idx + 1]));
      setTimelineScrollTrigger((t) => t + 1);
    }
  };
  const selectAndScroll = (iso) => {
    setSelectedDate(iso);
    setTimelineScrollTrigger((t) => t + 1);
    requestAnimationFrame(() => {
      dayHeroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // ---------- Export (kept for backup) ----------
  const copyLegAsText = () => {
    if (!leg) return;
    const legItems = Object.values(items).filter((it) => it.legId === leg.id);
    const days = [...new Set(legItems.map((it) => it.date))].sort();
    const statusLabel = (id) => STATUSES.find((s) => s.id === id)?.label ?? id;
    const timeLabel = (id) => TIME_SLOTS.find((s) => s.id === id)?.label ?? id;
    const catLabel = (id) => categories.find((c) => c.id === id)?.label ?? id;

    const lines = [];
    lines.push(`Trip: ${tripTitle}`);
    lines.push(`Leg: ${leg.emoji} ${leg.city}, ${leg.country}${leg.tentative ? " (tentative)" : ""}`);
    lines.push(`Dates: ${leg.start} to ${leg.end}`);
    lines.push("");

    if (days.length === 0) {
      lines.push("No items.");
    } else {
      for (const day of days) {
        const dayLabel = formatDayShort(fromISO(day));
        const dayItems = legItems
          .filter((it) => it.date === day)
          .sort((a, b) => TIME_SLOTS.findIndex((s) => s.id === a.timeSlot) - TIME_SLOTS.findIndex((s) => s.id === b.timeSlot));
        lines.push(`--- ${dayLabel} ---`);
        for (const it of dayItems) {
          const parts = [`[${catLabel(it.category)}]`, `[${statusLabel(it.status)}]`, `[${timeLabel(it.timeSlot)}]`, it.title];
          if (it.location) parts.push(`@ ${it.location}`);
          lines.push(parts.join(" "));
          if (it.notes) lines.push(`  Notes: ${it.notes}`);
          if (it.link) lines.push(`  Link: ${it.link}`);
          if (it.endDate && it.endDate !== it.date) lines.push(`  Until: ${formatDayShort(fromISO(it.endDate))}`);
        }
        lines.push("");
      }
    }

    navigator.clipboard.writeText(lines.join("\n")).catch(() => {});
  };

  const handleExport = () => {
    const payload = {
      app: "trip-planner",
      version: 5,
      exportedAt: new Date().toISOString(),
      tripId,
      legIds,
      title: tripTitle,
      legs,
      categories,
      itemCount: Object.keys(items).length,
      items,
    };
    setExportPayload(JSON.stringify(payload, null, 2));
  };

  // ---------- Landing → create trip ----------
  const handleCreate = async ({ title, categories, legs }) => {
    setCreating(true);
    setCreationError(null);
    try {
      const result = await createTrip({
        title,
        categories,
        legs: legs.map((l, i) => ({
          city: l.city, country: l.country, start: l.start, end: l.end,
          emoji: l.emoji, colour: l.colour, tentative: !!l.tentative, sortOrder: i,
          items: [],
        })),
      });
      setTripUrl(result.tripId, result.legIds, false);
      setRoute({ mode: "cloud", tripId: result.tripId, legIds: result.legIds });
    } catch (err) {
      if (err instanceof NetworkError) {
        setCreationError("Couldn't reach the server. Check your connection.");
      } else {
        setCreationError(err.message || "Couldn't create trip.");
      }
    } finally {
      setCreating(false);
    }
  };

  // ---------- Duplicate ----------
  const handleDuplicate = async ({ title, categories, legs }) => {
    setDuplicating(true);
    try {
      const result = await apiDuplicateTrip(tripId, { title, categories, legs });
      window.location.href = `${window.location.origin}/?trip=${result.tripId}&legs=${result.legIds.join(",")}`;
    } catch (err) {
      showToast("error", err.message || "Duplicate failed");
    } finally {
      setDuplicating(false);
    }
  };

  // ---------- Render ----------

  if (mode === "landing") {
    return (
      <LandingFlow onCreate={handleCreate} busy={creating} error={creationError} />
    );
  }

  if (splashState === "loading") {
    return <AirplaneLoader exiting={false} />;
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-sm bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-3">
            <AlertCircle className="text-red-500" size={20} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
            Couldn't load trip
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors"
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    {splashState === "exiting" && <AirplaneLoader exiting={true} />}
    <div
      className={`min-h-screen bg-stone-50 dark:bg-slate-900${darkMode ? " dark" : ""}`}
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        ...(splashState === "exiting" ? { animation: 'ap-app-in 0.7s ease 0.35s forwards', opacity: 0 } : {}),
      }}
    >
      <header className="px-5 pt-5 pb-3 bg-white dark:bg-slate-800 border-b border-slate-200/70 dark:border-slate-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 dark:text-slate-500">The Itinerary</div>
            <h1
              className="text-2xl font-semibold text-slate-900 dark:text-slate-100 leading-tight mt-0.5"
              style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: "-0.02em" }}
            >
              {tripTitle}
            </h1>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => setSettingsMenuOpen((o) => !o)}
                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
                title="Trip settings"
              >
                <Settings size={16} />
              </button>
              {settingsMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setSettingsMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-40 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <button
                      onClick={() => { setSettingsMenuOpen(false); setLegsModalOpen(true); }}
                      className="w-full px-3 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                    >
                      <MapPin size={14} className="text-slate-500 dark:text-slate-400" /> Trip legs
                    </button>
                    <button
                      onClick={() => { setSettingsMenuOpen(false); setMumModeOpen(true); }}
                      className="w-full px-3 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 border-t border-slate-100 dark:border-slate-700"
                    >
                      <Heart size={14} className="text-rose-500" /> Mum mode
                    </button>
                    <button
                      onClick={() => { setSettingsMenuOpen(false); setDuplicateModalOpen(true); }}
                      className="w-full px-3 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 border-t border-slate-100 dark:border-slate-700"
                    >
                      <Copy size={14} className="text-slate-500 dark:text-slate-400" /> Duplicate trip
                    </button>
                    <button
                      onClick={() => { setSettingsMenuOpen(false); handleExport(); }}
                      className="w-full px-3 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 border-t border-slate-100 dark:border-slate-700"
                    >
                      <Download size={14} className="text-slate-500 dark:text-slate-400" /> Export / backup
                    </button>
                    <div className="px-3 pt-2 pb-2 border-t border-slate-100 dark:border-slate-700 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
                      Categories are fixed at trip creation. Use Duplicate to start a new trip with different categories.
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setDarkMode((o) => !o)}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
              title="Toggle dark mode"
            >
              <Moon size={16} />
            </button>
            {leg && (
              <button
                onClick={copyLegAsText}
                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
                title="Copy leg as text (for LLM)"
              >
                <ClipboardCopy size={16} />
              </button>
            )}
            <button
              onClick={() => setShareModalOpen(true)}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
              title="Share trip"
            >
              <Share2 size={16} />
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
          <span>{allDays.length > 0 ? `${formatDayShort(allDays[0])} – ${formatDayShort(allDays[allDays.length - 1])}` : "No legs"}</span>
          <span className="w-1 h-1 rounded-full bg-slate-300" />
          <span>{allDays.length} days</span>
          <span className="w-1 h-1 rounded-full bg-slate-300" />
          <span>{Object.keys(items).length} items</span>
          <span className="w-1 h-1 rounded-full bg-slate-300" />
          <span className="inline-flex items-center gap-1" title={isOnline ? "Connected" : "Offline"}>
            {isOnline ? <Cloud size={11} /> : <CloudOff size={11} className="text-amber-600" />}
            {savingActivity > 0 ? "Saving…" : isOnline ? "Synced" : "Offline"}
            {queueSize > 0 && <span className="ml-1 text-amber-600">· {queueSize} queued</span>}
          </span>
        </div>
      </header>

      <div className="px-3 pt-2 bg-gradient-to-b from-white dark:from-slate-800 to-slate-50/50 dark:to-slate-900/50 flex justify-end gap-1">
        <button
          onClick={() => setCalendarView("timeline3")}
          className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition-all ${calendarView === "timeline3" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500"}`}
        >3 Day</button>
        <button
          onClick={() => setCalendarView("timeline")}
          className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition-all ${calendarView === "timeline" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500"}`}
        >5 Day</button>
      </div>

      {selectedDate && (
        <TimelineCalendar
          selectedDate={selectedDate}
          onSelect={selectAndScroll}
          items={items}
          allDays={allDays}
          legForDate={legForDate}
          categories={categories}
          darkMode={darkMode}
          colWidth={calendarView === "timeline3" ? 128 : 77}
          scrollTrigger={timelineScrollTrigger}
        />
      )}

      {selectedDate && (
        <>
          <div ref={dayHeroRef}>
            <DayHero date={selectedDate} leg={leg} tripStart={tripStart} totalDays={allDays.length} />
          </div>

          <div className="px-5 py-3 bg-white dark:bg-slate-800 border-b border-slate-200/70 dark:border-slate-700 flex items-center justify-between gap-2 sticky top-0 z-10">
            <div className="flex items-center gap-1">
              <button onClick={goPrev} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400">
                <ChevronLeft size={18} />
              </button>
              <button onClick={goNext} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400">
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              <button
                onClick={() => setFilter(null)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide border transition-all ${filter === null ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600"}`}
              >All</button>
              {sortedCategories.map((c) => {
                const Icon = resolveIcon(c.icon);
                const active = filter === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setFilter(active ? null : c.id)}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide border transition-all"
                    style={active
                      ? { background: c.colour, color: "white", borderColor: c.colour }
                      : { background: "white", color: c.colour, borderColor: `${c.colour}40` }}
                  >
                    <Icon size={11} />
                    {c.label.split(" ")[0]}
                  </button>
                );
              })}
            </div>
          </div>

          <main className="px-4 py-4 pb-28 space-y-5 bg-stone-50 dark:bg-slate-900">
            {dayItems.length === 0 ? (
              <EmptyState onAdd={() => { setEditing(null); setModalOpen(true); }} />
            ) : (
              groupedDay.map(({ cat, items: sectionItems }) => {
                const Icon = resolveIcon(cat.icon);
                const isAccom = cat.id === "accom";
                return (
                  <section key={cat.id}>
                    <div className="px-1 mb-2 flex items-center gap-2">
                      <Icon size={13} style={{ color: cat.colour }} />
                      <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: cat.colour }}>{cat.label}</h3>
                      <div className="flex-1 h-px ml-1" style={{ background: `${cat.colour}25` }} />
                      <span className="text-[10px] text-slate-400">{sectionItems.length}</span>
                    </div>
                    <div className="space-y-2">
                      {sectionItems.map((item) => (
                        <ItemCard
                          key={`${item.id}-${item._dayIndex || 0}`}
                          item={item}
                          sleepy={isAccom}
                          categories={categories}
                          onEdit={(it) => { setEditing(items[it.id] || it); setModalOpen(true); }}
                          onTitleClick={(it) => { setEditing({ ...(items[it.id] || it), _noAutoFocus: true }); setModalOpen(true); }}
                          onDelete={handleDelete}
                          onCycleStatus={handleCycleStatus}
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </main>

          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full bg-slate-900 text-white shadow-2xl flex items-center justify-center hover:bg-slate-700 hover:scale-105 active:scale-95 transition-all"
            style={{ boxShadow: "0 12px 30px -8px rgba(15, 23, 42, 0.5)" }}
          >
            <Plus size={24} />
          </button>
        </>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full shadow-lg text-sm text-white max-w-[90vw]"
             style={{ background: toast.kind === "error" ? "#b91c1c" : toast.kind === "warn" ? "#b45309" : "#0f172a" }}>
          {toast.message}
        </div>
      )}

      <ItemModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
        noAutoFocus={!!editing?._noAutoFocus}
        defaultDate={selectedDate || ""}
        allDays={allDays}
        legForDate={legForDate}
        categories={categories}
      />

      <ExportModal
        open={!!exportPayload}
        payload={exportPayload || ""}
        onClose={() => setExportPayload(null)}
      />

      <LegsModal
        open={legsModalOpen}
        legs={legs}
        tripId={tripId}
        onClose={() => setLegsModalOpen(false)}
        onSave={handleLegsSave}
      />

      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        tripId={tripId}
        legs={legs}
      />

      <MumModeModal
        open={mumModeOpen}
        onClose={() => setMumModeOpen(false)}
        tripTitle={tripTitle}
        legs={legs}
        items={items}
      />

      <DuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        currentTitle={tripTitle}
        currentCategories={categories}
        legs={legs}
        onSubmit={handleDuplicate}
        busy={duplicating}
      />
    </div>
    </>
  );
}
